import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAcceptInvitation } from '@/hooks/useOrganizationMembers';
import { useAuth } from '@clerk/clerk-react';

export const Route = createFileRoute('/invite/$token')({
  component: InviteAcceptPage,
});

function InviteAcceptPage() {
  const { token } = Route.useParams();
  const { isSignedIn } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accept = useAcceptInvitation();

  useEffect(() => {
    if (isSignedIn && !accepted && !accept.isPending) {
      accept.mutate({ token }, {
        onSuccess: () => {
          setAccepted(true);
          // Redirect handled by higher-level navigation; fallback to dashboard
          window.location.href = '/dashboard';
        },
        onError: (e: Error) => setError(e?.message || 'Failed to accept invitation')
      });
    }
  }, [isSignedIn, accepted, accept, token]);

  if (!isSignedIn) {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <Card className="max-w-lg mx-auto mt-16">
        <CardHeader>
          <CardTitle>Invitation</CardTitle>
          <CardDescription>Please sign in or sign up to accept this invitation.</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3">
          <Button asChild>
            <a href={`/sign-in?redirect_url=${redirect}`}>Sign in</a>
          </Button>
          <Button asChild variant="outline">
            <a href={`/sign-up?redirect_url=${redirect}`}>Sign up</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-lg mx-auto mt-16">
      <CardHeader>
        <CardTitle>Accepting Invitation</CardTitle>
        <CardDescription>{accept.isPending ? 'Processing…' : accepted ? 'Invitation accepted!' : (error || 'Ready')}</CardDescription>
      </CardHeader>
      <CardContent>
        {!accept.isPending && !accepted && (
          <Button onClick={() => accept.mutate({ token })}>Accept</Button>
        )}
      </CardContent>
    </Card>
  );
}
